/**
 * PII redaction for the logging pipeline and Sentry's `beforeSend`.
 *
 * Redaction is easy; proving it works is the deliverable. A deny-list that quietly
 * stops matching after a refactor is worse than none, because it creates confidence
 * without protection — hence the tests in `redact.test.ts` rather than a reviewer's
 * assurance.
 *
 * Two mechanisms, because either alone leaks:
 *   - by key name, which catches `password` wherever it appears
 *   - by value shape, which catches a JWT sitting in a field called `data`
 */
export const REDACTED = '[redacted]';
/** Matched case-insensitively against the key, as a substring. */
export const DENIED_KEYS = [
    'password',
    'secret',
    'token',
    'authorization',
    'apikey',
    'api_key',
    'cookie',
    'session',
    'email',
    'phone',
    'ssn',
    'creditcard',
];
const VALUE_PATTERNS = [
    /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/, // JWT
    /\bBearer\s+[A-Za-z0-9._~+/-]{16,}=*/i, // bearer token
    /\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/, // email address
    /\b(?:sk|pk)_(?:live|test)_[A-Za-z0-9]{16,}\b/, // vendor secret keys
    /\bpostgres(?:ql)?:\/\/[^\s"']+/i, // connection string with credentials
];
function keyIsDenied(key) {
    const lowered = key.toLowerCase();
    return DENIED_KEYS.some((denied) => lowered.includes(denied));
}
function redactString(value) {
    return VALUE_PATTERNS.some((pattern) => pattern.test(value))
        ? REDACTED
        : value;
}
/**
 * Returns a copy with denied values replaced. Handles nesting, arrays, and cycles —
 * cycles matter because a logged error often carries a request object that points
 * back at itself, and a naive walk hangs the process.
 */
export function redact(input) {
    return walk(input, new WeakSet());
}
function walk(value, seen) {
    if (typeof value === 'string')
        return redactString(value);
    if (value === null || typeof value !== 'object')
        return value;
    if (seen.has(value))
        return '[circular]';
    seen.add(value);
    if (Array.isArray(value))
        return value.map((item) => walk(item, seen));
    const output = {};
    for (const [key, nested] of Object.entries(value)) {
        output[key] = keyIsDenied(key) ? REDACTED : walk(nested, seen);
    }
    return output;
}
//# sourceMappingURL=redact.js.map